import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { MarkdownContent } from '@/components/markdown-content'
import { getMyPollResultRows } from '@/components/polls/poll-result-rows'
import { PollResults } from '@/components/polls/poll-results'
import { PollVoteForm } from '@/components/polls/poll-vote-form'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { markdownToPlainText } from '@/lib/markdown'
import { getMyPolls, type MyPoll } from '@/lib/polls-api'
import { queryKeys } from '@/lib/query-keys'

const statusBadges = {
  open: { label: 'Offen', className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  ended: { label: 'Beendet', className: 'bg-muted text-muted-foreground' },
}

// All started, published polls of the current member or trainer. Votes are
// only possible (and changeable) while a poll is open.
export function PollsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const pollsQuery = useQuery({
    queryKey: queryKeys.myPolls,
    queryFn: getMyPolls,
  })
  const polls = pollsQuery.data ?? []
  // Read from the query, so the dialog shows the vote and results after a refetch.
  const selected = polls.find((poll) => poll.id === selectedId) ?? null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Umfragen</h1>
        <p className="text-sm text-muted-foreground">
          Stimme bei offenen Umfragen ab. Deine Stimme kannst du ändern, solange die Umfrage läuft.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {pollsQuery.isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : pollsQuery.isError ? (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <p className="text-sm text-destructive">{getErrorMessage(pollsQuery.error)}</p>
              <Button variant="outline" size="sm" onClick={() => pollsQuery.refetch()}>
                Erneut laden
              </Button>
            </div>
          ) : polls.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Aktuell gibt es keine Umfragen.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zeitraum</TableHead>
                  <TableHead>Deine Stimme</TableHead>
                  <TableHead className="w-36" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {polls.map((poll) => {
                  const badge = poll.is_open ? statusBadges.open : statusBadges.ended

                  return (
                    <TableRow key={poll.id}>
                      <TableCell className="max-w-xs">
                        <button
                          type="button"
                          className="block w-full truncate text-left font-medium hover:underline"
                          onClick={() => setSelectedId(poll.id)}
                        >
                          {poll.title}
                        </button>
                        <div className="truncate text-xs text-muted-foreground">
                          {markdownToPlainText(poll.body)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={badge.className}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div>ab {formatDateTime(poll.visible_from ?? poll.published_at)}</div>
                        <div className="text-muted-foreground">
                          {poll.visible_until ? `bis ${formatDateTime(poll.visible_until)}` : 'unbegrenzt'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {poll.has_voted ? (
                          <Badge variant="outline">Abgestimmt</Badge>
                        ) : (
                          <span className="text-muted-foreground">
                            {poll.is_open ? 'Noch offen' : 'Nicht teilgenommen'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant={poll.is_open && !poll.has_voted ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedId(poll.id)}
                        >
                          {!poll.is_open ? 'Ansehen' : poll.has_voted ? 'Stimme ändern' : 'Abstimmen'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PollDetailsDialog poll={selected} onClose={() => setSelectedId(null)} />
    </div>
  )
}

function PollDetailsDialog({ poll, onClose }: { poll: MyPoll | null; onClose: () => void }) {
  const resultRows = poll ? getMyPollResultRows(poll) : null

  return (
    <Dialog open={poll !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        {poll && (
          <>
            <DialogHeader>
              <DialogTitle>{poll.title}</DialogTitle>
              <DialogDescription>
                {poll.is_open
                  ? poll.visible_until
                    ? `Offen bis ${formatDateTime(poll.visible_until)}`
                    : 'Offen'
                  : 'Beendet'}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[40vh] overflow-y-auto">
              <MarkdownContent>{poll.body}</MarkdownContent>
            </div>
            {(poll.is_open || poll.has_voted) && (
              <PollVoteForm
                poll={poll}
                readOnly={!poll.is_open}
                onVoted={() => toast.success('Danke für deine Stimme!')}
              />
            )}
            {resultRows && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium">
                  Ergebnis · {poll.voter_count === 1 ? '1 Person hat' : `${poll.voter_count} Personen haben`}{' '}
                  abgestimmt
                </h3>
                <PollResults rows={resultRows} voterCount={poll.voter_count ?? 0} />
              </div>
            )}
            {!poll.is_open && !poll.has_voted && !resultRows && (
              <p className="text-sm text-muted-foreground">Du hast an dieser Umfrage nicht teilgenommen.</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
